import {
  createApi,
  FetchArgs,
  fetchBaseQuery
} from '@reduxjs/toolkit/query/react'
import {omit} from 'ramda'

import {getBrandCodeFromUrlOrCookie} from '@vio/core/src/_web/brand/utils'
import {getUserSessionService} from '@vio/core/src/auth/services'

import {
  GetNotificationPreferencesParams,
  NotificationPreferencesResponse,
  UpdateSubscriptionParams,
  UpdateSubscriptionResponse
} from './types'

export * from './types'

const MEMBERSHIP_API_ENDPOINT =
  process.env.NEXT_PUBLIC_MEMBERSHIP_API_ENDPOINT ?? ''
const NOTIFICATIONS_PREFERENCE_API_URL = `${MEMBERSHIP_API_ENDPOINT}notification-preferences`
const NOTIFICATIONS_PREFERENCE_V2_API_URL = `${MEMBERSHIP_API_ENDPOINT}v2/notification-preferences`

// On /v2 identity comes from the JWT only; drop client-supplied identifiers so a
// stale email/token can never scope the response.
const stripIdentity = (args: string | FetchArgs): string | FetchArgs => {
  if (typeof args === 'string') return args
  const next = {...args}
  if (next.params) next.params = omit(['email', 'token'], next.params)
  if (next.body) next.body = omit(['email'], next.body)
  return next
}

export const notificationPreferencesApi = createApi({
  reducerPath: 'notificationPreferencesApi',
  tagTypes: ['NotificationPreferences'],
  baseQuery: async (args, api, extraOptions) => {
    const {idToken} = await getUserSessionService().catch(() => ({
      idToken: undefined
    }))

    // /v2 only accepts vio-pool JWTs; brands with their own pools stay on legacy
    const useV2 = Boolean(idToken) && getBrandCodeFromUrlOrCookie() === 'vio'

    const baseQuery = fetchBaseQuery({
      baseUrl: useV2
        ? NOTIFICATIONS_PREFERENCE_V2_API_URL
        : NOTIFICATIONS_PREFERENCE_API_URL,
      prepareHeaders: headers => {
        if (useV2) headers.set('Authorization', `Bearer ${idToken!.toString()}`)
        return headers
      }
    })
    return baseQuery(useV2 ? stripIdentity(args) : args, api, extraOptions)
  },
  endpoints: builder => ({
    getNotificationPreferences: builder.query<
      NotificationPreferencesResponse,
      GetNotificationPreferencesParams
    >({
      query: params => ({
        url: '',
        params
      }),
      providesTags: () => [
        // Use a single consistent tag for ALL notification preferences
        {type: 'NotificationPreferences', id: 'ALL'}
      ]
    }),
    updateSubscription: builder.mutation<
      UpdateSubscriptionResponse,
      UpdateSubscriptionParams
    >({
      query: body => ({
        url: '',
        method: 'PUT',
        body
      }),
      invalidatesTags: () => [{type: 'NotificationPreferences', id: 'ALL'}]
    })
  })
})

export const {
  useGetNotificationPreferencesQuery,
  useUpdateSubscriptionMutation
} = notificationPreferencesApi
